// ===========================================
// ROUTER CONFIGURATION
// ===========================================

import { lazy, Suspense, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Outlet,
} from 'react-router-dom';
import { adminRoutes } from './adminRoutes';
import { RootRedirect } from './RootRedirect';
import { DomainGuard } from './guards/DomainGuard';
import { LanguageGuard } from './guards/LanguageGuard';

// Layouts (lazy loaded)
const MainLayout = lazy(() => import('@/layouts/MainLayout'));
const AuthLayout = lazy(() => import('@/layouts/AuthLayout'));
const AccountLayout = lazy(() => import('@/layouts/AccountLayout'));

// Public Pages (lazy loaded)
const HomePage = lazy(() => import('@/pages/public/HomePage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const PackagesPage = lazy(() => import('@/pages/public/PackagesPage'));
const PackageDetailsPage = lazy(() => import('@/pages/public/PackageDetailsPage'));
const ToursPage = lazy(() => import('@/pages/public/ToursPage'));
const ToursByDestinationPage = lazy(() => import('@/pages/public/ToursByDestinationPage'));
const ExplorePage = lazy(() => import('@/pages/public/ExplorePage'));
const ShoreExcursionsPage = lazy(() => import('@/pages/public/ShoreExcursionsPage'));
const BlogPage = lazy(() => import('@/pages/public/Blog/BlogPage').then(module => ({ default: module.BlogPage })));
const BlogPostPage = lazy(() => import('@/pages/public/Blog/BlogPostPage').then(module => ({ default: module.BlogPostPage })));

// Booking Pages (lazy loaded)
const BookingGuestDetailsPage = lazy(() => import('@/pages/public/BookingGuestDetailsPage'));
const BookingPaymentPage = lazy(() => import('@/pages/public/BookingPaymentPage'));
const BookingConfirmationPage = lazy(() => import('@/pages/public/BookingConfirmationPage'));

// Auth Pages (lazy loaded)
const GoogleCallbackPage = lazy(() => import('@/pages/auth/GoogleCallbackPage'));

// Account Pages (lazy loaded)
const DashboardPage = lazy(() => import('@/pages/account/DashboardPage'));
const ProfilePage = lazy(() => import('@/pages/account/ProfilePage'));
const BookingsPage = lazy(() => import('@/pages/account/BookingsPage'));
const BookingDetailsPage = lazy(() => import('@/pages/account/BookingDetailsPage'));
const FavoritesPage = lazy(() => import('@/pages/account/FavoritesPage'));
const SettingsPage = lazy(() => import('@/pages/account/SettingsPage'));
const ReviewsPage = lazy(() => import('@/pages/account/ReviewsPage'));
const SupportPage = lazy(() => import('@/pages/account/SupportPage'));

// Error Pages (lazy loaded)
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));
const ServerErrorPage = lazy(() => import('@/pages/errors/ServerErrorPage'));

// Guards
import { GuestGuard } from './guards/GuestGuard';

// Loading fallback
// eslint-disable-next-line react-refresh/only-export-components
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
  </div>
);

// Suspense wrapper component
// eslint-disable-next-line react-refresh/only-export-components
const SuspenseWrapper = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

/**
 * Create router with all routes
 * URL structure: /:lang/page (e.g. /en/home, /fr/tours, /ar/packages)
 */
export const router = createBrowserRouter([
  // Admin routes (loaded via domain detection)
  ...adminRoutes,

  // Google OAuth callback — no lang prefix (registered URI must stay fixed)
  {
    path: 'auth/callback',
    element: (
      <SuspenseWrapper>
        <GoogleCallbackPage />
      </SuspenseWrapper>
    ),
  },

  // Public routes
  {
    element: (
      <DomainGuard requireAdmin={false}>
        <Outlet />
      </DomainGuard>
    ),
    children: [
      // Root redirect → /{lang}/home
      {
        path: '/',
        element: <RootRedirect />,
      },

      // All lang-prefixed public routes
      {
        path: ':lang',
        element: (
          <LanguageGuard>
            <Outlet />
          </LanguageGuard>
        ),
        children: [
          // Pages with MainLayout (header + footer)
          {
            element: (
              <SuspenseWrapper>
                <MainLayout />
              </SuspenseWrapper>
            ),
            children: [
              {
                path: 'home',
                element: (
                  <SuspenseWrapper>
                    <HomePage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'about',
                element: (
                  <SuspenseWrapper>
                    <AboutPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'contact',
                element: (
                  <SuspenseWrapper>
                    <ContactPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'packages',
                element: (
                  <SuspenseWrapper>
                    <PackagesPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'packages/:slug',
                element: (
                  <SuspenseWrapper>
                    <PackageDetailsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'tours',
                element: (
                  <SuspenseWrapper>
                    <ToursPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'tours/destination/:slug',
                element: (
                  <SuspenseWrapper>
                    <ToursByDestinationPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'explore',
                element: (
                  <SuspenseWrapper>
                    <ExplorePage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'tours/:slug',
                element: (
                  <SuspenseWrapper>
                    <PackageDetailsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'shore-excursions',
                element: (
                  <SuspenseWrapper>
                    <ShoreExcursionsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'shore-excursions/:slug',
                element: (
                  <SuspenseWrapper>
                    <PackageDetailsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'blog',
                element: (
                  <SuspenseWrapper>
                    <BlogPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'blog/:slug',
                element: (
                  <SuspenseWrapper>
                    <BlogPostPage />
                  </SuspenseWrapper>
                ),
              },
              // Booking flow
              {
                path: 'booking/guest-details',
                element: (
                  <SuspenseWrapper>
                    <BookingGuestDetailsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'booking/payment',
                element: (
                  <SuspenseWrapper>
                    <BookingPaymentPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'booking/confirmation',
                element: (
                  <SuspenseWrapper>
                    <BookingConfirmationPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Auth routes (with lang prefix for consistency)
          {
            path: 'auth',
            children: [
              {
                element: (
                  <GuestGuard>
                    <SuspenseWrapper>
                      <AuthLayout />
                    </SuspenseWrapper>
                  </GuestGuard>
                ),
                children: [],
              },
            ],
          },

          // Account routes
          {
            path: 'account',
            element: (
              <SuspenseWrapper>
                <AccountLayout />
              </SuspenseWrapper>
            ),
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <DashboardPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'profile',
                element: (
                  <SuspenseWrapper>
                    <ProfilePage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'bookings',
                element: (
                  <SuspenseWrapper>
                    <BookingsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'bookings/:id',
                element: (
                  <SuspenseWrapper>
                    <BookingDetailsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'favorites',
                element: (
                  <SuspenseWrapper>
                    <FavoritesPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'reviews',
                element: (
                  <SuspenseWrapper>
                    <ReviewsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'support',
                element: (
                  <SuspenseWrapper>
                    <SupportPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: 'settings',
                element: (
                  <SuspenseWrapper>
                    <SettingsPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },
        ],
      },

      // Error pages (no lang prefix needed)
      {
        path: '404',
        element: (
          <SuspenseWrapper>
            <NotFoundPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '500',
        element: (
          <SuspenseWrapper>
            <ServerErrorPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Catch-all → 404
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },
]);
